/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Directive, ElementRef, Injectable, Injector, Input, OnDestroy, OnInit, Renderer2, forwardRef} from '@angular/core';

import {ControlValueAccessor, NG_VALUE_ACCESSOR} from './control_value_accessor';
import {NgControl} from './ng_control';

export const /** @type {?} */ RADIO_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => RadioControlValueAccessor),
  multi: true
};
/**
 * Internal class used by Angular to uncheck radio buttons with the matching name.
 */
export class RadioControlRegistry {
private _accessors: any[] = [];
/**
 * @param {?} control
 * @param {?} accessor
 * @return {?}
 */
add(control: NgControl, accessor: RadioControlValueAccessor) {
    this._accessors.push([control, accessor]);
  }
/**
 * @param {?} accessor
 * @return {?}
 */
remove(accessor: RadioControlValueAccessor) {
    for (let /** @type {?} */ i = this._accessors.length - 1; i >= 0; --i) {
      if (this._accessors[i][1] === accessor) {
        this._accessors.splice(i, 1);
        return;
      }
    }
  }
/**
 * @param {?} accessor
 * @return {?}
 */
select(accessor: RadioControlValueAccessor) {
    this._accessors.forEach((c) => {
      if (this._isSameGroup(c, accessor) && c[1] !== accessor) {
        c[1].fireUncheck(accessor.value);
      }
    });
  }
/**
 * @param {?} controlPair
 * @param {?} accessor
 * @return {?}
 */
private _isSameGroup(
      controlPair: [NgControl, RadioControlValueAccessor],
      accessor: RadioControlValueAccessor): boolean {
    if (!controlPair[0].control) return false;
    return controlPair[0]._parent === accessor._control._parent &&
        controlPair[1].name === accessor.name;
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function RadioControlRegistry_tsickle_Closure_declarations() {
/** @type {?} */
RadioControlRegistry.decorators;
/**
 * @nocollapse
 * @type {?}
 */
RadioControlRegistry.ctorParameters;
/** @type {?} */
RadioControlRegistry.prototype._accessors;
}

/**
 * \@whatItDoes Writes radio control values and listens to radio control changes.
 * 
 * Used by {\@link NgModel}, {\@link FormControlDirective}, and {\@link FormControlName}
 * to keep the view synced with the {\@link FormControl} model.
 * 
 * \@howToUse 
 * 
 * If you have imported the {\@link FormsModule} or the {\@link ReactiveFormsModule}, this
 * value accessor will be active on any radio control that has a form directive. You do
 * **not** need to add a special selector to activate it.
 * 
 * ### How to use radio buttons with form directives
 * 
 * To use radio buttons in a template-driven form, you'll want to ensure that radio buttons
 * in the same group have the same `name` attribute.  Radio buttons with different `name`
 * attributes do not affect each other.
 * 
 * {\@example forms/ts/radioButtons/radio_button_example.ts region='TemplateDriven'}
 * 
 * When using radio buttons in a reactive form, radio buttons in the same group should have the
 * same `formControlName`. You can also add a `name` attribute, but it's optional.
 * 
 * {\@example forms/ts/reactiveRadioButtons/reactive_radio_button_example.ts region='Reactive'}
 * 
 *  * **npm package**: `\@angular/forms`
 * 
 *  \@stable
 */
export class RadioControlValueAccessor implements ControlValueAccessor,
    OnDestroy, OnInit {
/**
 * \@internal
 */
_state: boolean;
/**
 * \@internal
 */
_control: NgControl;
/**
 * \@internal
 */
_fn: Function;
  onChange = () => {};
  onTouched = () => {};

   name: string;
   formControlName: string;
   value: any;
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 * @param {?} _registry
 * @param {?} _injector
 */
constructor(
private _renderer: Renderer2,
private _elementRef: ElementRef,
private _registry: RadioControlRegistry,
private _injector: Injector) {}
/**
 * @return {?}
 */
ngOnInit(): void {
    this._control = this._injector.get(NgControl);
    this._checkName();
    this._registry.add(this._control, this);
  }
/**
 * @return {?}
 */
ngOnDestroy(): void { this._registry.remove(this); }
/**
 * @param {?} value
 * @return {?}
 */
writeValue(value: any): void {
    this._state = value === this.value;
    this._renderer.setProperty(this._elementRef.nativeElement, 'checked', this._state);
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnChange(fn: (_: any) => {}): void {
    this._fn = fn;
    this.onChange = () => {
      fn(this.value);
      this._registry.select(this);
    };
  }
/**
 * @param {?} value
 * @return {?}
 */
fireUncheck(value: any): void { this.writeValue(value); }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnTouched(fn: () => {}): void { this.onTouched = fn; }
/**
 * @param {?} isDisabled
 * @return {?}
 */
setDisabledState(isDisabled: boolean): void {
    this._renderer.setProperty(this._elementRef.nativeElement, 'disabled', isDisabled);
  }
/**
 * @return {?}
 */
private _checkName(): void {
    if (this.name && this.formControlName && this.name !== this.formControlName) {
      this._throwNameError();
    }
    if (!this.name && this.formControlName) this.name = this.formControlName;
  }
/**
 * @return {?}
 */
private _throwNameError(): void {
    throw new Error(`
      If you define both a name and a formControlName attribute on your radio button, their values
      must match. Ex: <input type="radio" formControlName="food" name="food">
    `);
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector:
      'input[type=radio][formControlName],input[type=radio][formControl],input[type=radio][ngModel]',
  host: {'(change)': 'onChange()', '(blur)': 'onTouched()'},
  providers: [RADIO_VALUE_ACCESSOR]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer2, },
{type: ElementRef, },
{type: RadioControlRegistry, },
{type: Injector, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'name': [{ type: Input },],
'formControlName': [{ type: Input },],
'value': [{ type: Input },],
};
}

function RadioControlValueAccessor_tsickle_Closure_declarations() {
/** @type {?} */
RadioControlValueAccessor.decorators;
/**
 * @nocollapse
 * @type {?}
 */
RadioControlValueAccessor.ctorParameters;
/** @type {?} */
RadioControlValueAccessor.propDecorators;
/**
 * \@internal
 * @type {?}
 */
RadioControlValueAccessor.prototype._state;
/**
 * \@internal
 * @type {?}
 */
RadioControlValueAccessor.prototype._control;
/**
 * \@internal
 * @type {?}
 */
RadioControlValueAccessor.prototype._fn;
/** @type {?} */
RadioControlValueAccessor.prototype.onChange;
/** @type {?} */
RadioControlValueAccessor.prototype.onTouched;
/** @type {?} */
RadioControlValueAccessor.prototype.name;
/** @type {?} */
RadioControlValueAccessor.prototype.formControlName;
/** @type {?} */
RadioControlValueAccessor.prototype.value;
/** @type {?} */
RadioControlValueAccessor.prototype._renderer;
/** @type {?} */
RadioControlValueAccessor.prototype._elementRef;
/** @type {?} */
RadioControlValueAccessor.prototype._registry;
/** @type {?} */
RadioControlValueAccessor.prototype._injector;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}



// WEBPACK FOOTER //
// ../../../../packages/forms/src/directives/radio_control_value_accessor.ts